import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, filter, of, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { MessageTypes } from '../services/front-end/notification.service';

class FeError {
  constructor(public message: string, public type: string, public data?: any) {}

  toString = (): string => this.message;
}

const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const TokenInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);

  if (request.url.indexOf('/api/') === -1) {
    return next(request);
  }

  const token = authService.getAccessToken();
  if (token) {
    request = addToken(request, token);
  }

  return next(request).pipe(
    switchMap((event: any) => {
      if (event instanceof HttpResponse) {
        if (event.headers.has('Content-Type') && event.headers.get('Content-Type')?.includes('text/html')) {
          return of(event);
        }

        if (event.body.code === 401 && event.body.type === 'ERROR_TOKEN_EXPIRED') {
          return handle401Error(request, next, authService);
        }

        if (!event.body.status) {
          throw new FeError(event.body.message, 'error', event.body);
        }
      }
      return of(event);
    })
  );
};

const addToken = (request: HttpRequest<any>, token: string) => {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
};

const handle401Error = (request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService) => {
  if (!isRefreshing.value) {
    isRefreshing.next(true);
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response: any) => {
        isRefreshing.next(false);
        refreshTokenSubject.next(response.result.access_token);
        return next(addToken(request, response.result.access_token));
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next(addToken(request, token!)))
    );
  }
};
